import { Injectable } from '@angular/core';
import {
  IPlaceSuggestion,
  IResolvedPlaceAddress,
} from '../interfaces/resolved-place-address.interface';

declare const google: any;

const SUGGESTION_LIMIT = 5;

@Injectable()
export class AddressHelper {

  extractUnitNumber(addressLine1?: string, addressLine2?: string): string {
    const line2 = (addressLine2 || '').trim();
    if (line2) {
      return line2;
    }
    const match = (addressLine1 || '').match(/(?:\b(?:APT|APARTMENT|UNIT|STE|SUITE|LOT)\b\.?|#)\s*([A-Za-z0-9-]+)\s*$/i);
    return match ? match[1] : '';
  }

  zip5(zip?: string): string {
    return (zip || '').replace(/\D/g, '').slice(0, 5);
  }

  isPlacesAvailable(): boolean {
    return typeof google !== 'undefined'
      && !!google.maps
      && typeof google.maps.importLibrary === 'function';
  }

  async resolveAddress(query: string): Promise<IResolvedPlaceAddress | null> {
    if (!query || !this.isPlacesAvailable()) {
      return null;
    }

    try {
      const placesLibrary: any = await google.maps.importLibrary('places');
      const response = await placesLibrary.AutocompleteSuggestion.fetchAutocompleteSuggestions({
        input: query,
        includedRegionCodes: ['us'],
      });

      const suggestion = response && response.suggestions && response.suggestions[0];
      const prediction = suggestion && suggestion.placePrediction;
      if (!prediction || !prediction.toPlace) {
        return null;
      }

      const place = await prediction.toPlace();
      await place.fetchFields({
        fields: ['formattedAddress', 'location', 'addressComponents'],
      });

      if (!place || !place.location) {
        return null;
      }

      return this.parsePlace(place);
    } catch (err) {
      return null;
    }
  }

  async suggestAddresses(query: string): Promise<IPlaceSuggestion[]> {
    if (!query || !this.isPlacesAvailable()) {
      return [];
    }

    try {
      const placesLibrary: any = await google.maps.importLibrary('places');
      const response = await placesLibrary.AutocompleteSuggestion.fetchAutocompleteSuggestions(
        {
          input: query,
          includedRegionCodes: ['us'],
        },
      );
      const suggestions = (response && response.suggestions) || [];

      return suggestions
        .slice(0, SUGGESTION_LIMIT)
        .map((suggestion: any) => this.toSuggestion(suggestion))
        .filter((suggestion: IPlaceSuggestion) => !!suggestion);
    } catch (err) {
      return [];
    }
  }

  private toSuggestion(suggestion: any): IPlaceSuggestion {
    const prediction = suggestion && suggestion.placePrediction;

    if (!prediction || !prediction.toPlace) {
      return null;
    }

    return {
      text: this.predictionText(prediction),
      resolve: () => this.resolvePrediction(prediction),
    };
  }

  private predictionText(prediction: any): string {
    if (prediction.text && prediction.text.text) {
      return prediction.text.text;
    }

    return prediction.text || '';
  }

  private async resolvePrediction(
    prediction: any,
  ): Promise<IResolvedPlaceAddress> {
    try {
      const place = await prediction.toPlace();

      if (!place) {
        return null;
      }

      await place.fetchFields({
        fields: ['formattedAddress', 'location', 'addressComponents'],
      });

      if (!place.location) {
        return null;
      }

      return this.parsePlace(place);
    } catch (err) {
      return null;
    }
  }

  private parsePlace(place: any): IResolvedPlaceAddress {
    const components = place.addressComponents || [];
    let streetNumber = '';
    let route = '';
    let city = '';
    let state = '';
    let zip = '';

    components.forEach((component: any) => {
      const types = component.types || [];
      if (types.indexOf('street_number') !== -1) {
        streetNumber = component.longText || component.shortText || '';
      }
      if (types.indexOf('route') !== -1) {
        route = component.longText || component.shortText || '';
      }
      if (types.indexOf('locality') !== -1) {
        city = component.longText || component.shortText || '';
      }
      if (types.indexOf('neighborhood') !== -1 && !city) {
        city = component.longText || component.shortText || '';
      }
      if (types.indexOf('administrative_area_level_1') !== -1) {
        state = component.shortText || component.longText || '';
      }
      if (types.indexOf('postal_code') !== -1) {
        zip = component.longText || component.shortText || '';
      }
    });

    const street = (streetNumber && route)
      ? streetNumber + ' ' + route
      : (route || streetNumber || place.formattedAddress || '');
    const location = place.location;
    const latitude = typeof location.lat === 'function' ? location.lat() : location.lat;
    const longitude = typeof location.lng === 'function' ? location.lng() : location.lng;

    return { street, city, state, zip, latitude, longitude };
  }
}



// WEBPACK FOOTER //
// ./src/app/core/helpers/address-helper.service.ts